import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useState, useEffect } from 'react';
import { NavLink as RouterLink, useLocation } from 'react-router-dom';
// @mui
import {
  AppBar,
  Box,
  Container,
  Link,
  Stack,
  Toolbar,
  Typography,
  Select,
  MenuItem,
  Button,
  Popover,
  TextField,
  IconButton,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import { styled, useTheme } from '@mui/material/styles';
// hooks
import useOffSetTop from '../../../hooks/useOffSetTop';
import useResponsive from '../../../hooks/useResponsive';
// utils
import cssStyles from '../../../utils/cssStyles';
import axios from '../../../utils/axios';
// config
import { HEADER, NAVBAR } from '../../../config';
// components
import Iconify from '../../../components/Iconify';
import Logo from '../../../components/Logo';
import { IconButtonAnimate } from '../../../components/animate';
//
import useAuth from '../../../hooks/useAuth';
import { openModal } from '../../../redux/slices/authModal';
import AccountPopover from './AccountPopover';
import navConfig from './MenuConfig';
import MenuDesktop from './MenuDesktop';
import MenuMobile from './MenuMobile';
import NotificationsPopover from './NotificationsPopover';
import SpotlightTrigger from '../../../components/spotlight/SpotlightTrigger';
import CreditsIndicator from './CreditsIndicator';
import MemberCampusSwitcher from './MemberCampusSwitcher';
import { useCampus } from '../../../hooks/useCampus';
import { useRefetchTrigger } from '../../../hooks/useRefetchTrigger';
import VisitsIndicator from './VisitsIndicator';
import HelpButtonPopover from './HelpButtonPopover';
import OrgPaymentBanner from './OrgPaymentBanner';
import AutomationProgress from './AutomationProgress';
import { usePageCampus } from '../../../contexts/PageCampusContext';

// ----------------------------------------------------------------------

// Utility function to normalize time format (remove seconds)
const normalizeTime = (timeString) => {
  if (!timeString) return '';
  // If time has seconds (HH:MM:SS), remove them
  return timeString.substring(0, 5);
};

const RootStyle = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'isCollapse' && prop !== 'isOffset' && prop !== 'verticalLayout',
})(({ isCollapse, isOffset, verticalLayout, theme }) => ({
  ...cssStyles(theme).bgBlur(),
  boxShadow: 'none',
  height: HEADER.MOBILE_HEIGHT,
  zIndex: theme.zIndex.appBar + 1,
  transition: theme.transitions.create(['width', 'height'], {
    duration: theme.transitions.duration.shorter,
  }),
  [theme.breakpoints.up('lg')]: {
    height: HEADER.DASHBOARD_DESKTOP_HEIGHT,
    width: `calc(100% - ${NAVBAR.DASHBOARD_WIDTH + 1}px)`,
    ...(isCollapse && {
      width: `calc(100% - ${NAVBAR.DASHBOARD_COLLAPSE_WIDTH}px)`,
    }),
    ...(isOffset && {
      height: HEADER.DASHBOARD_DESKTOP_OFFSET_HEIGHT,
    }),
    ...(verticalLayout && {
      width: '100%',
      height: HEADER.DASHBOARD_DESKTOP_OFFSET_HEIGHT,
      backgroundColor: theme.palette.background.default,
    }),
  },
}));

const ToolbarStyle = styled(Toolbar)(({ theme }) => ({
  height: HEADER.MOBILE_HEIGHT,
  transition: theme.transitions.create(['height', 'background-color'], {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.shorter,
  }),
  [theme.breakpoints.up('md')]: {
    height: HEADER.MAIN_DESKTOP_HEIGHT,
  },
}));

const ToolbarShadowStyle = styled('div')(({ theme }) => ({
  left: 0,
  right: 0,
  bottom: 0,
  height: 24,
  zIndex: -1,
  margin: 'auto',
  borderRadius: '50%',
  position: 'absolute',
  width: `calc(100% - 48px)`,
  boxShadow: theme.customShadows.z8,
}));

// ----------------------------------------------------------------------

DashboardHeader.propTypes = {
  onOpenSidebar: PropTypes.func,
  isCollapse: PropTypes.bool,
  verticalLayout: PropTypes.bool,
};

const CampusSelector = styled(Select)(({ theme }) => ({
  // Mobile-first: keep the switcher compact so it doesn't crowd the top bar.
  marginRight: theme.spacing(1),
  minWidth: 140,
  maxWidth: 170,
  height: 40,
  backgroundColor: theme.palette.common.white,
  color: theme.palette.text.primary,
  [theme.breakpoints.up('lg')]: {
    marginRight: theme.spacing(2),
    minWidth: 200,
    maxWidth: 'none',
  },
  '.MuiSelect-select': {
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(2),
    [theme.breakpoints.up('lg')]: {
      paddingLeft: theme.spacing(2),
    },
  },
  '&.MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: theme.palette.grey[300],
    },
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
    },
  },
  '& .MuiSvgIcon-root': {
    color: theme.palette.grey[600],
  },
}));

const SingleCampusDisplay = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  maxWidth: 170,
  marginRight: theme.spacing(1),
  padding: theme.spacing(1, 1.5),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.common.white,
  border: `1px solid ${theme.palette.grey[300]}`,
  [theme.breakpoints.up('lg')]: {
    maxWidth: 'none',
    marginRight: theme.spacing(2),
    padding: theme.spacing(1, 2),
  },
  '& .MuiSvgIcon-root': {
    marginRight: theme.spacing(1),
    fontSize: 20,
    color: theme.palette.primary.main,
    flexShrink: 0,
  },
}));

const StatusButton = styled(Button)(({ theme, status }) => ({
  minWidth: 130,
  height: 40,
  backgroundColor: status === 'active' ? theme.palette.success.main : theme.palette.warning.main,
  color: theme.palette.common.white,
  '&:hover': {
    backgroundColor: status === 'active' ? theme.palette.success.dark : theme.palette.warning.dark,
  },
  '&:disabled': {
    backgroundColor: theme.palette.grey[300],
    color: theme.palette.grey[500],
  },
  textTransform: 'none',
  fontWeight: 600,
}));

const TimeAdjustButton = styled(IconButton)(({ theme }) => ({
  width: 40,
  height: 40,
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.grey[300]}`,
  '&:hover': {
    backgroundColor: theme.palette.grey[100],
    borderColor: theme.palette.primary.main,
  },
}));

// Restaurant Admin Controls Component
function RestaurantAdminControls() {
  const { user } = useAuth();
  const theme = useTheme();
  const [timeAnchorEl, setTimeAnchorEl] = useState(null);
  const [cafeteriaStatus, setCafeteriaStatus] = useState('active');
  const [openingTime, setOpeningTime] = useState('09:00');
  const [closingTime, setClosingTime] = useState('21:00');
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const assignedCafeteria = user?.assigned_cafeterias?.[0];

  useEffect(() => {
    if (assignedCafeteria) {
      setCafeteriaStatus(assignedCafeteria.cafeteria_status || 'active');
      // setOpeningTime('09:00');
      // setClosingTime('21:00');
    }
  }, [assignedCafeteria]);

  const handleToggleStatus = async () => {
    if (!assignedCafeteria) return;

    setLoading(true);
    try {
      const newStatus = cafeteriaStatus === 'active' ? 'inactive' : 'active';

      await axios.patch(`/api/v1/cafe/admin/cafeteria/${assignedCafeteria.cafeteria_id}/`, {
        status: newStatus,
      });

      setCafeteriaStatus(newStatus);
      setSnackbar({
        open: true,
        message: 'Status updated successfully!',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error updating cafeteria status:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update status. Please try again.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTimeClick = (event) => {
    setTimeAnchorEl(event.currentTarget);
  };

  const handleTimeClose = () => {
    setTimeAnchorEl(null);
  };

  const handleSaveTimes = async () => {
    if (!assignedCafeteria) return;

    setLoading(true);
    try {
      // Get all days of the week
      const daysOfWeek = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

      // Create operating hours for all days with the same times
      const cleanedHours = daysOfWeek.map((day) => ({
        day_of_week: day,
        opening_time: normalizeTime(openingTime) || '09:00',
        closing_time: normalizeTime(closingTime) || '18:00',
        is_closed: false,
      }));

      const submitData = new FormData();
      submitData.append('operating_hours', JSON.stringify(cleanedHours));

      await axios.put(`/api/v1/cafe/admin/cafeteria/${assignedCafeteria.cafeteria_id}/`, submitData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setSnackbar({
        open: true,
        message: 'Operating hours updated successfully!',
        severity: 'success',
      });
      handleTimeClose();
    } catch (error) {
      console.error('Error updating operating hours:', error);
      const errorMessage =
        Object.values(error.response?.data || {})
          .flat()
          .join(' ') || 'Failed to update operating hours.';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const timePopoverOpen = Boolean(timeAnchorEl);

  if (!assignedCafeteria) {
    return null;
  }

  return (
    <>
      {/* Status Toggle Button */}
      <StatusButton
        status={cafeteriaStatus}
        onClick={handleToggleStatus}
        disabled={loading}
        startIcon={
          loading ? (
            <CircularProgress size={18} color="inherit" />
          ) : cafeteriaStatus === 'active' ? (
            <PauseCircleIcon />
          ) : (
            <PlayCircleIcon />
          )
        }
      >
        {loading ? 'Updating...' : cafeteriaStatus === 'active' ? 'Set Inactive' : 'Set Active'}
      </StatusButton>
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default function DashboardHeader({ onOpenSidebar, isCollapse = false, verticalLayout = false }) {
  const { pathname } = useLocation();

  const dispatch = useDispatch();

  const isOffset = useOffSetTop(HEADER.DASHBOARD_DESKTOP_HEIGHT) && !verticalLayout;

  const isDesktop = useResponsive('up', 'lg');

  const theme = useTheme();

  const { user } = useAuth();

  const isHome = false;

  const finalConfig = navConfig;

  const [open, setOpen] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);

  const { activeCampus, allCampuses, setActiveCampus } = useCampus();
  const { triggerRefetch } = useRefetchTrigger();
  const { pageCampusMode } = usePageCampus();

  const handleCampusChange = (event) => {
    const newCampus = event.target.value;
    setActiveCampus(newCampus);
    triggerRefetch();
  };

  useEffect(() => {
    if (drawerOpen) {
      handleDrawerClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const handleOpen = () => {
    setOpen(!open);
  };

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  return (
    <RootStyle isCollapse={isCollapse} isOffset={isOffset} verticalLayout={verticalLayout}>
      <Toolbar
        sx={{
          minHeight: '100% !important',
          px: { xs: 1, sm: 2 }, // Minimal padding on mobile
        }}
      >
        <Container
          maxWidth={false}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: { xs: 0.5, sm: 2 },
          }}
        >
          {/* {isDesktop && <Logo sx={{ mr: 2.5 }} />} */}

          {!isDesktop && (
            <IconButtonAnimate onClick={onOpenSidebar} sx={{ mr: 1, color: 'text.primary' }}>
              <Iconify icon="eva:menu-2-fill" />
            </IconButtonAnimate>
          )}

          {user?.role === 'org' && <OrgPaymentBanner user={user}/>}

          {/* Ctrl+K spotlight. Renders only inside OrgLayout's provider. */}
          {user?.role === 'org' && isDesktop && <SpotlightTrigger />}

          <Box sx={{ flexGrow: 1 }} />
          {user?.role === 'org' && !isDesktop && <SpotlightTrigger />}
          {/* Renders nothing until one of this user's own actions sets an
              automation off, so the bar is unchanged the rest of the time. */}
          <AutomationProgress />
          {/* Members get the bell too. Until now a join request or a visitor
              at reception reached them as one FCM push and nothing else, so
              clearing the tray lost it. */}
          {(user?.role === 'org' || user?.role === 'user') && <NotificationsPopover />}
          {user?.role === 'org' && isDesktop && <HelpButtonPopover />}

          {pageCampusMode ? (
            user?.role === 'org' && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                  border: '1px dashed',
                  borderColor: 'divider',
                  opacity: 0.7,
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                  Campus
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 600, lineHeight: 1.2, color: 'primary.main' }}>
                  Handled in page
                </Typography>
              </Box>
            )
          ) : user?.role === 'org' && allCampuses.length > 1 ? (
            <CampusSelector
              value={activeCampus}
              onChange={handleCampusChange}
              displayEmpty
              MenuProps={{
                PaperProps: { sx: { maxHeight: 320, maxWidth: '85vw', mt: 1 } },
              }}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                  <LocationOnIcon sx={{ mr: 1, fontSize: 20, color: theme.palette.primary.main, flexShrink: 0 }} />
                  <Typography color="text.primary" noWrap>
                    {selected ? selected.name : 'Select a Campus'}
                  </Typography>
                </Box>
              )}
            >
              {allCampuses.map((campus) => (
                <MenuItem
                  key={campus.id}
                  value={campus}
                  sx={{
                    '&:hover': {
                      backgroundColor: theme.palette.grey[50],
                    },
                  }}
                >
                  <LocationOnIcon sx={{ mr: 1, fontSize: 20, color: theme.palette.primary.main, flexShrink: 0 }} />
                  <Typography color="text.primary" noWrap>
                    {campus.name}
                  </Typography>
                </MenuItem>
              ))}
            </CampusSelector>
          ) : (
            user?.role === 'org' &&
            allCampuses.length === 1 && (
              <SingleCampusDisplay>
                <LocationOnIcon />
                <Typography variant="subtitle2" color="text.primary" noWrap>
                  {allCampuses[0].name}
                </Typography>
              </SingleCampusDisplay>
            )
          )}

          {user?.role === 'user' && <MemberCampusSwitcher />}

          <Stack direction="row" alignItems="center" spacing={{ xs: 0.5, sm: 1.5 }}>
            {/* <LanguagePopover /> */}
            {/* {user.role === 'university' && <HelpButtonPopover />} */}
            {/* <NotificationsPopover /> */}
            {/* <ContactsPopover /> */}
            {isDesktop && <MenuDesktop isOffset={isOffset} isHome={isHome} navConfig={finalConfig} />}

            {!isDesktop && (
              <MenuMobile
                isOffset={isOffset}
                isHome={isHome}
                navConfig={finalConfig}
                drawerOpen={drawerOpen}
                handleDrawerClose={handleDrawerClose}
              />
            )}
            {/* <CreditsIndicator /> */}
            {/* {user?.role === 'user' && user?.userVisitLeft !== null && <VisitsIndicator />} */}

            {/* Restaurant Admin Controls */}
            {user?.role === 'restaurant_admin' && user?.assigned_cafeterias?.length > 0 && <RestaurantAdminControls />}

            <AccountPopover />
          </Stack>
        </Container>
      </Toolbar>

      {isOffset && <ToolbarShadowStyle />}
    </RootStyle>
  );
}
